import { INotice, Notifier } from '@airbrake/browser';
import { INoticeFrame } from '@airbrake/browser/dist/notice';
import React from 'react';
import config from "../../config";
import store, { StoreActions } from "../../state/store";

const Assert = require("assert");

//built in airbrake severities, do not modify
export const enum ErrorSeverity	{
	Debug = "debug",
	Info = "info",
	Notice = "notice",
	Warning = "warning",
	Error = "error",
	Critical = "critical",
	Alert = "alert",
	Emergency = "emergency"
}

//custom error categories, modify as needed
export const enum ErrorCategory	{
	General = "general",
	ProjectLoad = "project-load",
	ProjectSave = "project-save",
	Network = "network",
	Unknown = "unknown"
}

class ErrorHandler
{
	airbrake?: Notifier = undefined;

	constructor() 
	{
		if (config.AIRBRAKE && config.AIRBRAKE_PROJECT_ID && config.AIRBRAKE_PROJECT_KEY)
		{
			this.airbrake = new Notifier({
				projectId: Number(config.AIRBRAKE_PROJECT_ID),
				projectKey: config.AIRBRAKE_PROJECT_KEY,
				environment: config.AIRBRAKE_ENVIRONMENT ?? "local",
			});
			console.log("Airbrake Initialized");

			//adding the following payload to all airbrake reports
			this.airbrake.addFilter((notice) => 
			{
				// completely ignore errors from certain unwanted files
				if(filterFromFiles(notice))
				{
					return null;
				}

				// ensure we have the context set up correctly
				if(!notice.context) notice.context = {};
				if(!notice.context.category)
				{
					categorizeAndSetSeverity(notice);
				}
				
				notice.context.version = config.CREATE_VERSION;
				const userSessionInfo = store.getState().userModule.sessionInfo;
				if(userSessionInfo)
				{
					notice.context.user = {
						id: userSessionInfo.userId,
						name: userSessionInfo.name,
						email: userSessionInfo.email,
						username: userSessionInfo.username,
					};
				}
				return notice;
			});

			function traverseNoticeBacktrace(notice: INotice, callback: (frame: INoticeFrame) => void)
			{
				if (!notice.errors) return;

				for (const error of notice.errors) 
				{
					if (!error.backtrace) continue;
					
					for (const frame of error.backtrace) 
					{
						if (frame.file)
						{
							callback(frame);
						}
					}
				}
			}

			// filter out errors from unwanted files (e.g. grapesjs static files)
			function filterFromFiles(notice: INotice)
			{
				const filesToFilter = [
					"/grapesjs/",
				];

				let shouldFilterError = false;
				traverseNoticeBacktrace(notice, (frame) =>
				{
					if (filesToFilter.some((file) => frame.file.includes(file)))
					{
						shouldFilterError = true;
					}
				});

				return shouldFilterError;
			}

			function categorizeAndSetSeverity(notice: INotice) 
			{
				const filesToSetAsWarning = [
					"node_modules",
				];
	
				traverseNoticeBacktrace(notice, (line) =>
				{
					if (filesToSetAsWarning.some((file) => line.file.includes(file)))
					{
						notice.context.severity = ErrorSeverity.Warning;		
						notice.context.category = ErrorCategory.General;				
					}
				});

				if(notice.context.category) return;

				const message = notice.errors?.[0].message.toLowerCase() || ""; 
				const type = notice.errors?.[0].type || "";

				//TODO: add specific error messages to categorize. e.g if there is a repeating error we wish to treat as warning, add it here
				if (type === "AxiosError" || message.includes("timeout")) 
				{
					notice.context.category = ErrorCategory.Network;
					notice.context.severity = ErrorSeverity.Error;
				}
				else if(
					type === "Error" ||
					type === "TypeError" || 
					type === "ReferenceError" || 
					type === "SyntaxError" || 
					type === "RangeError" || 
					type === "URIError" || 
					type === "EvalError" || 
					type ==="Invariant Violation")
				{
					notice.context.category = ErrorCategory.General;
					notice.context.severity = ErrorSeverity.Error;
				}
				else 
				{
					notice.context.category = ErrorCategory.Unknown;
					notice.context.severity = ErrorSeverity.Error; 
				}
			}
		}
	}

	reportError(error: unknown, category?: ErrorCategory, severity?: ErrorSeverity) 
	{	
		console.error(error);

		if (this.airbrake) 
		{
			const serializeError = (error: unknown) =>
			{
				if (error instanceof Error) 
				{
					return error.message;
				}
				return JSON.stringify(error ?? "Unknown Error");
			}

			this.airbrake.notify({
				error: serializeError(error),
				context: {
					severity: severity,
					category: category 
				},
			});
		}
	}

	assert(value: any, message = "Assertion Failed", category?: ErrorCategory, severity?: ErrorSeverity) 
	{
		try
		{
			Assert(value, message);
		}
		catch (error)
		{
			this.reportError(error, category, severity);

			if (config.DISABLE_CRASH_REPORT) return;

			store.dispatch(StoreActions.application.setCrashError({ message: message ?? "An error has occured" }));
		}
	}

	wrapElement(element: () => React.ReactNode)
	{
		if (this.airbrake) 
		{
			return this.airbrake.wrap(element);
		}
		return element;
	}
}

const errorHandlerInstance = new ErrorHandler();

export function assert(value: any, message = "Assertion Failed", category = ErrorCategory.General)
{
	try
	{
		Assert(value, message);
	}
	catch (error)
	{
		errorHandlerInstance.reportError(error, category);

		if (!config.DISABLE_CRASH_REPORT)
		{
			store.dispatch(StoreActions.application.setCrashError({ message: message ?? "An error has occured" }));
		}
	}
}

export const getErrorMessage = (error: unknown) => 
{
	if(typeof error === 'string')
	{
		return error;
	}
	if(error instanceof Error)
	{
		return error.message;
	}
	return 'Unknown Error';
}



export default errorHandlerInstance;